<template>
  <div class="podcast-container" :style="backgroundStyle">
    <!-- Main stage area for active speaker - more natural design -->
    <div class="main-stage" v-if="activeSpeaker && showMainStage">
      <div class="active-speaker-container">
        <div class="active-speaker-wrapper" :class="{ 'is-playing': isPlaying }">
          <!-- Speaker image container with relative positioning -->
          <div class="active-speaker-image-container">
            <img
              :src="getActiveSpeakerImage()"
              :alt="`${activeSpeaker} avatar`"
              class="active-speaker-image"
            />

            <!-- Add the wave animation indicator (only visible when playing) -->
            <div
              v-if="isPlaying"
              class="active-image-wave-indicator"
              :style="{ backgroundColor: `rgba(0, 0, 0, 0.6)`, borderColor: getTextColor(getActiveSpeakerIndex()) }"
            >
              <div
                v-for="n in 5"
                :key="`main-wave-${n}`"
                class="main-wave-bar"
                :style="{ backgroundColor: getTextColor(getActiveSpeakerIndex()) }"
              ></div>
            </div>
          </div>

          <div class="active-speaker-info">
            <span class="active-speaker-name">{{ getActiveSpeakerObj().name }}</span>
            <span class="active-speaker-role">{{ getActiveSpeakerObj().role }}</span>
          </div>
          <!-- Enhanced speaking animation -->
          <div v-if="isPlaying" class="audio-visualizer">
            <div v-for="n in 5" :key="`vis-${n}`" class="visualizer-bar"
                :style="{ animationDelay: `${n * 0.1}s`, backgroundColor: getTextColor(getActiveSpeakerIndex()) }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Minimalist speaker strip at bottom, always shown -->
    <div class="speaker-strip" :class="{ 'standalone': alwaysShowSpeakers }">
      <div
        v-for="(speaker, index) in speakers"
        :key="`speaker-${index}`"
        class="speaker-thumbnail"
        :class="{
          'active': activeSpeaker === speaker.name,
          'is-speaking': activeSpeaker === speaker.name && isPlaying
        }"
        @click="$emit('speaker-selected', speaker.name)"
      >
        <div class="thumbnail-wrapper">
          <img
            :src="getSpeakerImage(index)"
            :alt="`${speaker.name} avatar`"
            class="thumbnail-image"
          />

          <!-- Mini wave animation when speaking -->
          <div
            v-if="activeSpeaker === speaker.name && isPlaying"
            class="thumbnail-wave-indicator"
          >
            <div
              v-for="n in 3"
              :key="`thumb-wave-${n}`"
              class="thumbnail-wave-bar"
              :style="{ backgroundColor: getTextColor(index) }"
            ></div>
          </div>
        </div>

        <span
          class="thumbnail-name"
          :style="{ color: activeSpeaker === speaker.name ? getTextColor(index) : '' }"
        >
          {{ speaker.name }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SpeakerPanel',
  props: {
    isPro: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    messageId: {
      required: true
    },
    alwaysShowSpeakers: {
      type: Boolean,
      default: false
    },
    minimizeWhenInactive: {
      type: Boolean,
      default: false
    },
    speakers: {
      type: Array,
      required: true,
      default: () => []
    },
    activeSpeaker: {
      type: String,
      default: null
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    isExpandedView: {
      type: Boolean,
      default: true
    },
    // Props to receive from parent
    uploadedImages: {
      type: Object,
      default: () => ({})
    },
    backgroundImage: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showMainStage: false,
      // Array of fallback images that will be used for speakers
      allImages: [
        '/static/Gemini_Generated_Image_yznx90yznx90yznx.jpeg',
        '/static/Gemini_Generated_Image_3uyccg3uyccg3uyc.jpeg',
        '/static/Gemini_Generated_Image_r53bp9r53bp9r53b.jpeg',
        '/static/Gemini_Generated_Image_ns59fwns59fwns59.jpeg',
        '/static/Gemini_Generated_Image_yznx90yznx90yznx.jpeg',
        '/static/Gemini_Generated_Image_m68ngom68ngom68n.jpeg',
        '/static/Gemini_Generated_Image_qc0oqtqc0oqtqc0o.jpeg',
      ],
      colors: [
        { bg: '#E3F2FD', text: '#1E88E5' }, // Blue
        { bg: '#F3E5F5', text: '#8E24AA' }, // Purple
        { bg: '#E8F5E9', text: '#43A047' }, // Green
        { bg: '#FFF3E0', text: '#FB8C00' }, // Orange
        { bg: '#F3E5F5', text: '#5E35B1' }, // Deep Purple
        { bg: '#E0F2F1', text: '#00897B' }, // Teal
        { bg: '#FBE9E7', text: '#F4511E' }, // Deep Orange
        { bg: '#F1F8E9', text: '#7CB342' }  // Light Green
      ],
    };
  },
  computed: {
    backgroundStyle() {
      return this.backgroundImage ? {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${this.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {};
    }
  },

  methods: {
    // Get the active speaker object
    getActiveSpeakerObj() {
      if (!this.activeSpeaker) return {};
      return this.speakers.find(speaker => speaker.name === this.activeSpeaker) || {};
    },

    // Get the active speaker index
    getActiveSpeakerIndex() {
      if (!this.activeSpeaker) return -1;
      return this.speakers.findIndex(speaker => speaker.name === this.activeSpeaker);
    },

    // Get active speaker image
    getActiveSpeakerImage() {
      const index = this.getActiveSpeakerIndex();
      if (index === -1) return '';
      return this.getSpeakerImage(index);
    },

    // Get the image for a speaker (uploaded or fallback)
    getSpeakerImage(index) {
      // Create a unique key using messageId and index
      const uniqueKey = `${this.messageId}_${index}`;

      // Return the uploaded image for this specific combination if it exists
      return this.uploadedImages[uniqueKey] || this.allImages[index % this.allImages.length];
    },

    getTextColor(index) {
      if (index < 0) return '#1E88E5'; // Default color
      const color = this.colors[index % this.colors.length];
      return color.text;
    },
  }
}
</script>

<style scoped>
/* Main container - simplified for natural look */
.podcast-container {
  position: relative;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/static/Gemini_Generated_Image_kn0ojzkn0ojzkn0o.jpeg');
  background-size: cover;
  background-position: center;
  overflow: hidden;
  border-radius: 12px;
  transition: background 0.5s ease;
  background-color: #f8fafc;
  max-height: 100%;
}

.theme--dark .podcast-container {
  background-color: #0f172a;
}

/* Main stage area - more spacious and natural */
.main-stage {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  overflow: hidden;
  width: 100%;
  max-height: 40vh; /* Prevent excessive height */
}

.active-speaker-container {
  width: 100%;
  max-width: 800px; /* Wider to use more horizontal space */
  margin: 0 auto;
  transition: all 0.3s ease;
}

/* Removed box styling for a more natural look */
.active-speaker-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  transition: all 0.3s ease;
  gap: 1.5rem;
}

.active-speaker-image {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Make image glow subtly when speaking */
.is-playing .active-speaker-image {
  box-shadow: 0 0 20px 5px rgba(255, 255, 255, 0.3);
}

.active-speaker-info {
  text-align: left;
  flex: 1;
}

.active-speaker-name {
  display: block;
  font-size: 1.75rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.25rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.active-speaker-role {
  display: block;
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* Audio visualizer - more subtle and integrated */
.audio-visualizer {
  display: flex;
  align-items: flex-end;
  height: 50px;
  gap: 3px;
}

.visualizer-bar {
  width: 4px;
  height: 5px;
  background-color: white;
  border-radius: 2px;
  animation: equalizer 1.2s ease-in-out infinite;
}

.visualizer-bar:nth-child(1) { animation-duration: 0.9s; }
.visualizer-bar:nth-child(2) { animation-duration: 1.3s; }
.visualizer-bar:nth-child(3) { animation-duration: 1.0s; }
.visualizer-bar:nth-child(4) { animation-duration: 1.5s; }
.visualizer-bar:nth-child(5) { animation-duration: 1.2s; }

/* Speaker strip - redesigned for better flow */
.speaker-strip {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10;
  overflow-x: auto;
  width: 100%;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.theme--dark .speaker-strip {
  background: rgba(15, 23, 42, 0.7);
}

/* Speaker thumbnails - more natural design */
.speaker-thumbnail {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0.5rem 0.75rem;
  border-radius: 24px;
  background: transparent;
}

.speaker-thumbnail.active {
  background: rgba(255, 255, 255, 0.15);
}

.speaker-thumbnail:hover {
  background: rgba(255, 255, 255, 0.1);
}

.thumbnail-wrapper {
  position: relative;
  width: 60px;
  height: 60px;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.speaker-thumbnail.active .thumbnail-image {
  box-shadow: 0 0 0 2px var(--speaker-color, white);
}

/* Wave indicator when speaking - simplified */
.thumbnail-wave-indicator {
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  padding: 2px;
  display: flex;
  gap: 2px;
}

.thumbnail-wave-bar {
  width: 3px;
  height: 3px;
  border-radius: 1px;
  background: white;
  animation: thumbnailWave 1s ease-in-out infinite;
}

.thumbnail-wave-bar:nth-child(1) { animation-delay: -0.3s; }
.thumbnail-wave-bar:nth-child(2) { animation-delay: -0.15s; }
.thumbnail-wave-bar:nth-child(3) { animation-delay: 0s; }

@keyframes thumbnailWave {
  0%, 100% { height: 3px; }
  50% { height: 7px; }
}

.thumbnail-name {
  margin-left: 0.75rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
}

.speaker-thumbnail.active .thumbnail-name {
  color: white;
  font-weight: 500;
}

/* Background upload - keep existing styles */
.background-upload {
  width: 100%;
}

.upload-bg-label {
  display: block;
  cursor: pointer;
  width: 100%;
}

.bg-preview-container {
  width: 100%;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #cbd5e0;
  background-color: #f8fafc;
  transition: all 0.3s ease;
}

.theme--dark .bg-preview-container {
  border-color: #475569;
  background-color: #1e293b;
}

.bg-preview-container:hover {
  border-color: #3b82f6;
  background-color: #f1f5f9;
}

.theme--dark .bg-preview-container:hover {
  border-color: #60a5fa;
  background-color: #334155;
}

.bg-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bg-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #64748b;
}

.theme--dark .bg-placeholder {
  color: #94a3b8;
}

.bg-placeholder svg {
  opacity: 0.7;
}

/* Speaker customization grid - keep existing styles */
.speakers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.speaker-customize-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.speaker-image-upload {
  width: 100%;
}

.preview-container {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #cbd5e0;
  background-color: #f8fafc;
  margin: 0 auto;
  transition: all 0.3s ease;
}

.theme--dark .preview-container {
  border-color: #475569;
  background-color: #1e293b;
}

.preview-container:hover {
  border-color: #3b82f6;
  transform: scale(1.05);
}

.theme--dark .preview-container:hover {
  border-color: #60a5fa;
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
  text-align: center;
  padding: 0.5rem;
}

.theme--dark .upload-placeholder {
  color: #94a3b8;
}

.file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
}

.speaker-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.name-input, .role-input, .voice-select {
  width: 100%;
  padding: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  transition: all 0.3s;
  background-color: #ffffff;
  color: #1a202c;
}

.theme--dark .name-input,
.theme--dark .role-input,
.theme--dark .voice-select {
  border-color: #475569;
  background-color: #1e293b;
  color: #e2e8f0;
}

.name-input:focus,
.role-input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.theme--dark .name-input:focus,
.theme--dark .role-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
}

.voice-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;
  padding-right: 2rem;
}

.theme--dark .voice-select {
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%239CA3AF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes equalizer {
  0%, 100% { height: 5px; }
  50% { height: 30px; }
}

/* Responsive styles */
@media (max-width: 768px) {
  .main-stage {
    padding: 1rem;
  }

  .active-speaker-wrapper {
    padding: 0.75rem;
    flex-direction: column;
    text-align: center;
  }

  .active-speaker-image {
    width: 120px;
    height: 120px;
    margin-bottom: 0.75rem;
  }

  .active-speaker-info {
    text-align: center;
  }

  .active-speaker-name {
    font-size: 1.25rem;
  }

  .active-speaker-role {
    font-size: 0.875rem;
  }

  .audio-visualizer {
    margin-top: 0.75rem;
    height: 30px;
  }


  .speakers-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 1rem;
  }

  .speaker-strip {
    padding: 0.75rem 0.5rem;
  }

  .speaker-thumbnail {
    padding: 0.35rem 0.5rem;
  }

  .thumbnail-wrapper {
    width: 32px;
    height: 32px;
  }

  .thumbnail-name {
    font-size: 0.75rem;
    margin-left: 0.5rem;
  }
}

@media (max-width: 480px) {
  .active-speaker-image {
    width: 150px;
    height: 150px;
  }

  .speakers-grid {
    grid-template-columns: 1fr 1fr;
  }

  .speaker-strip {
    justify-content: space-around;
  }
}
.active-speaker-image-container {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: visible; /* Allow the wave indicator to be visible outside the container */
}

.active-speaker-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Wave indicator for the main speaker image */
.active-image-wave-indicator {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 12px;
  padding: 4px;
  display: flex;
  gap: 3px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 5;
}

.main-wave-bar {
  width: 4px;
  height: 4px;
  border-radius: 2px;
  background: white;
  animation: mainWaveAnimation 1.2s ease-in-out infinite;
}

.main-wave-bar:nth-child(1) { animation-delay: -0.4s; }
.main-wave-bar:nth-child(2) { animation-delay: -0.3s; }
.main-wave-bar:nth-child(3) { animation-delay: -0.2s; }
.main-wave-bar:nth-child(4) { animation-delay: -0.1s; }
.main-wave-bar:nth-child(5) { animation-delay: 0s; }

@keyframes mainWaveAnimation {
  0%, 100% { height: 4px; }
  50% { height: 16px; }
}

/* Update responsive styles for main speaker image */
@media (max-width: 768px) {
  .active-speaker-image-container {
    width: 160px;
    height: 160px;
    margin-bottom: 0.75rem;
  }

  .active-image-wave-indicator {
    bottom: 5px;
    right: 5px;
    padding: 3px;
    gap: 2px;
  }

  .main-wave-bar {
    width: 3px;
  }
}

@media (max-width: 480px) {
  .active-speaker-image-container {
    width: 120px;
    height: 120px;
  }

  .active-image-wave-indicator {
    padding: 2px;
    gap: 2px;
  }

  .main-wave-bar {
    width: 2px;
  }
}

/* Enhanced voice select dropdown styling */
.voice-select {
  width: 100%;
  padding: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  transition: all 0.3s;
  background-color: #ffffff;
  color: #1a202c;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;
  padding-right: 2rem;
}

.theme--dark .voice-select {
  border-color: #475569;
  background-color: #1e293b;
  color: #e2e8f0;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%239CA3AF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
}

.voice-select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.theme--dark .voice-select:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
}

/* Style for the option groups */
.voice-select optgroup {
  font-weight: 600;
  font-style: normal;
  color: #64748b;
  background-color: #f8fafc;
}

.theme--dark .voice-select optgroup {
  color: #94a3b8;
  background-color: #1e293b;
}

/* Style for the options */
.voice-select option {
  font-weight: normal;
  color: #1a202c;
  background-color: #ffffff;
}

.theme--dark .voice-select option {
  color: #e2e8f0;
  background-color: #334155;
}

/* Highlight the selected voice */
.voice-select-container {
  position: relative;
}

.voice-select-container::after {
  content: attr(data-selected-voice);
  position: absolute;
  top: -18px;
  right: 8px;
  font-size: 10px;
  background-color: #3b82f6;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}

.voice-select-container:hover::after {
  opacity: 1;
}

/* When a voice is selected, show a small indicator */
.voice-selected .voice-select-label {
  color: #3b82f6;
}

.theme--dark .voice-selected .voice-select-label {
  color: #60a5fa;
}

.voice-selected .voice-select-label svg {
  color: #3b82f6;
  opacity: 1;
}

.theme--dark .voice-selected .voice-select-label svg {
  color: #60a5fa;
}
.transcript-customization {
  margin-bottom: 1.5rem;
}

.customization-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #334155;
}

.theme--dark .customization-label {
  color: #e2e8f0;
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.opacity-slider {
  flex: 1;
  height: 6px;
  -webkit-appearance: none;
  background: #e2e8f0;
  border-radius: 3px;
  outline: none;
}

.theme--dark .opacity-slider {
  background: #475569;
}

.opacity-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
}

.opacity-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
}

.theme--dark .opacity-slider::-webkit-slider-thumb,
.theme--dark .opacity-slider::-moz-range-thumb {
  background: #60a5fa;
}

.opacity-value {
  width: 40px;
  font-size: 0.875rem;
  text-align: right;
}

.color-picker-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.current-color {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #cbd5e0;
}

.theme--dark .current-color {
  border-color: #475569;
}

.color-picker {
  -webkit-appearance: none;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: transparent;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 4px;
}

.reset-button {
  margin-left: auto;
  background: #f1f5f9;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.theme--dark .reset-button {
  background: #334155;
  color: #94a3b8;
}

.reset-button:hover {
  background: #e2e8f0;
  color: #475569;
}

.theme--dark .reset-button:hover {
  background: #475569;
  color: #e2e8f0;
}
</style>
